import { useEffect, useRef, useState } from 'react';
import type { CalculatorClient } from '../api/client';
import {
  ApiCallError,
  type CarFuel,
  type DistanceResponse,
  type EventosInputs,
  type TransportMode,
} from '../api/types';
import { CityAutocomplete } from './CityAutocomplete';
import { Field, RadioChip, SelectInput, TextInput } from './FormControls';

interface Props {
  value: EventosInputs;
  onChange: (patch: Partial<EventosInputs>) => void;
  onNext: () => void;
  errors: Partial<Record<keyof EventosInputs, string>>;
  /** Cidade onde o evento acontece (vem do schema). Se setada, é mostrada ao lado
   * de "origem" e dispara cálculo automático de distância via geocoding. */
  eventCity?: string;
  /** Client + token pra disparar /distance. Opcional pra manter o componente
   * testável fora do contexto da App (test renders passam plain props). */
  client?: CalculatorClient;
  token?: string;
}

const TRANSPORT_OPTIONS: { value: TransportMode; label: string; hint: string }[] = [
  { value: 'aviao', label: 'Avião', hint: 'Voos comerciais' },
  { value: 'carro', label: 'Carro', hint: 'Próprio ou alugado' },
  { value: 'onibus', label: 'Ônibus', hint: 'Rodoviário' },
  { value: 'metro_trem', label: 'Metrô ou trem', hint: 'Transporte sobre trilhos' },
  { value: 'bike', label: 'Bicicleta', hint: 'Não emite' },
  { value: 'a_pe', label: 'A pé', hint: 'Não emite' },
];

const CAR_FUELS: { value: CarFuel; label: string }[] = [
  { value: 'gasolina', label: 'Gasolina' },
  { value: 'etanol', label: 'Etanol' },
  { value: 'diesel', label: 'Diesel' },
  { value: 'eletrico', label: 'Elétrico' },
];

type DistanceState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; km: number; method?: DistanceResponse['method'] }
  | { kind: 'error'; msg: string };

/** Helper text do campo distância — o texto muda conforme a estratégia que o
 * backend usou. `method` ausente = backend antigo (skew de deploy): mantém a
 * redação de linha reta, que era a verdade naquela versão. */
function distanceHint(
  method: DistanceResponse['method'] | undefined,
  origin: string,
  eventCity: string,
): string {
  const suffix = `entre ${origin} e ${eventCity} (você pode ajustar)`;
  switch (method) {
    case 'route':
      return `Distância rodoviária ${suffix}`;
    case 'haversine_adjusted':
      return `Distância rodoviária estimada ${suffix}`;
    default:
      return `Distância em linha reta ${suffix}`;
  }
}

export function StepDeslocamento({
  value, onChange, onNext, errors, eventCity, client, token,
}: Props) {
  const showCarFuel = value.transport_mode === 'carro';
  const hasEventCity = !!eventCity && eventCity.trim() !== '';

  // ── Auto-distance ──────────────────────────────────────────────────
  const [distState, setDistState] = useState<DistanceState>({ kind: 'idle' });
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const distanceTouchedRef = useRef(false); // user editou km manualmente

  useEffect(() => {
    // Sem cidade evento ou sem client → modo manual puro.
    if (!hasEventCity || !client || !token) return;
    const origin = value.origin_city?.trim() ?? '';
    if (origin.length < 3) {
      setDistState({ kind: 'idle' });
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Controller no escopo do efeito (não do timeout) pra que a cleanup aborte
    // request em voo: trocar de modal com uma consulta pendente faria a
    // resposta antiga sobrescrever a nova.
    const ctrl = new AbortController();
    debounceRef.current = setTimeout(() => {
      setDistState({ kind: 'loading' });
      client.computeDistance(token, origin, value.transport_mode, ctrl.signal)
        .then((d) => {
          setDistState({ kind: 'ok', km: d.distance_km, method: d.method });
          // Só auto-preenche se o user ainda não tocou no campo manualmente.
          if (!distanceTouchedRef.current) {
            onChange({ distance_km: d.distance_km });
          }
        })
        .catch((err) => {
          if (err.name === 'AbortError') return;
          const msg = err instanceof ApiCallError && err.code === 'invalid_input'
            ? 'Cidade não encontrada. Informe a distância em km.'
            : 'Não conseguimos calcular automaticamente. Informe a distância em km.';
          setDistState({ kind: 'error', msg });
        });
    }, 600);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      ctrl.abort();
    };
    // `transport_mode` entra nas deps de propósito: a distância depende do
    // modal (rota rodoviária vs linha reta). Debounce + cache server-side
    // tornam o re-fetch barato.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.origin_city, value.transport_mode, hasEventCity, client, token]);

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        onNext();
      }}
    >
      <div>
        <p className="text-xs uppercase tracking-wider text-tabaco mb-2">
          Como você foi ao evento?
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {TRANSPORT_OPTIONS.map((opt) => (
            <RadioChip
              key={opt.value}
              name="transport_mode"
              value={opt.value}
              checked={value.transport_mode === opt.value}
              onChange={(v) => onChange({ transport_mode: v as TransportMode })}
              label={opt.label}
              hint={opt.hint}
            />
          ))}
        </div>
        {errors.transport_mode && (
          <p className="text-2xs text-cobre-700 mt-1.5">{errors.transport_mode}</p>
        )}
      </div>

      {showCarFuel && (
        <Field label="Combustível do carro" required error={errors.car_fuel}>
          <SelectInput
            value={value.car_fuel ?? ''}
            onChange={(e) => onChange({ car_fuel: (e.target.value || undefined) as CarFuel })}
          >
            <option value="">Selecione…</option>
            {CAR_FUELS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </SelectInput>
        </Field>
      )}

      {/* Origem ↔ Destino — destino aparece quando event_city está configurada */}
      {hasEventCity ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field
            label="De qual cidade você veio?"
            hint="Comece a digitar e selecione a cidade"
          >
            <CityAutocomplete
              id="origin-city"
              value={value.origin_city ?? ''}
              onChange={(v) => onChange({ origin_city: v })}
              client={client}
              token={token}
              placeholder="Ex.: São Paulo"
            />
          </Field>
          <Field label="Cidade do evento" hint="Destino configurado pela organização">
            <TextInput
              type="text"
              value={eventCity!}
              readOnly
              tabIndex={-1}
              className="bg-cream/40 text-ink-muted cursor-default"
            />
          </Field>
        </div>
      ) : (
        <Field label="Cidade de origem (opcional)" hint="Apenas pra estatística — não armazenamos nome">
          <TextInput
            type="text"
            maxLength={80}
            value={value.origin_city ?? ''}
            onChange={(e) => onChange({ origin_city: e.target.value })}
            placeholder="Ex.: São Paulo"
          />
        </Field>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
        <Field
          label="Distância (one-way, em km)"
          required
          hint={
            distState.kind === 'loading' ? 'Calculando…'
            : distState.kind === 'ok' ? distanceHint(distState.method, value.origin_city?.trim() ?? '', eventCity ?? '')
            : distState.kind === 'error' ? distState.msg
            : 'De onde você saiu até o evento'
          }
          error={errors.distance_km}
        >
          <TextInput
            type="number"
            inputMode="decimal"
            min={0}
            step="0.1"
            value={value.distance_km || ''}
            onChange={(e) => {
              distanceTouchedRef.current = true;
              onChange({ distance_km: parseFloat(e.target.value) || 0 });
            }}
            placeholder="Ex.: 380"
          />
        </Field>
        <label className="flex items-center gap-2 pb-2.5">
          <input
            type="checkbox"
            checked={value.round_trip}
            onChange={(e) => onChange({ round_trip: e.target.checked })}
            className="size-4 accent-floresta"
          />
          <span className="text-sm text-ink">Ida e volta</span>
        </label>
      </div>

      <div className="pt-2 flex justify-end">
        <button
          type="submit"
          className="px-5 py-2.5 rounded-md bg-floresta text-cream text-sm font-medium hover:bg-floresta-600 transition-colors"
        >
          Continuar →
        </button>
      </div>
    </form>
  );
}
